import { useRef, useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useSupabaseSession } from "@/lib/SupabaseSessionContext";
import { Button } from "@/components/ui/button";
import { Camera, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Client-side image resize + compress + direct-upload to Supabase Storage.
 *
 * - bucket: 'avatars' or 'banners' (RLS enforces <uid>/<file> ownership)
 * - kind:   visual variant — round avatar vs wide banner crop
 *
 * The file path convention "<uid>/<file>" matches the storage RLS policies
 * created in supabase_schema_v2.sql, so the upload is rejected unless the
 * authenticated user is uploading into their own folder. We also delete old
 * objects in that folder after success to avoid orphaned files in the bucket.
 */
const TARGETS = {
  avatars: { w: 512, h: 512, profile_col: "avatar_url" },
  banners: { w: 1500, h: 500, profile_col: "banner_url" },
};
const MAX_BYTES = 200 * 1024; // 200KB after compression

async function resizeToBlob(file, targetW, targetH) {
  const bitmap = await createImageBitmap(file);
  const srcW = bitmap.width, srcH = bitmap.height;
  const srcAR = srcW / srcH, tgtAR = targetW / targetH;
  // cover-crop: keep target AR, then scale
  let sx = 0, sy = 0, sW = srcW, sH = srcH;
  if (srcAR > tgtAR) {
    // source is wider → crop sides
    sW = Math.round(srcH * tgtAR);
    sx = Math.round((srcW - sW) / 2);
  } else if (srcAR < tgtAR) {
    // source is taller → crop top/bottom
    sH = Math.round(srcW / tgtAR);
    sy = Math.round((srcH - sH) / 2);
  }
  const canvas = document.createElement("canvas");
  canvas.width = targetW; canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, sx, sy, sW, sH, 0, 0, targetW, targetH);

  // Iteratively reduce quality until under MAX_BYTES (min 0.55)
  let quality = 0.9;
  let blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  while (blob && blob.size > MAX_BYTES && quality > 0.55) {
    quality -= 0.1;
    blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  }
  return blob;
}

export default function AvatarBannerUploader({ bucket, currentUrl, onUploaded, isDE = true }) {
  const cfg = TARGETS[bucket];
  const inputRef = useRef(null);
  const { ready, supabaseUserId } = useSupabaseSession();
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentUrl || null);

  useEffect(() => { setPreviewUrl(currentUrl || null); }, [currentUrl]);

  if (!cfg) return null;
  const isAvatar = bucket === "avatars";

  const handleFile = async (file) => {
    if (!file || !ready || !supabaseUserId) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error(isDE ? "Nur PNG, JPG oder WebP." : "PNG, JPG or WebP only.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(isDE ? "Datei zu groß (max. 8 MB)." : "File too large (max 8 MB).");
      return;
    }
    setBusy(true);
    try {
      const blob = await resizeToBlob(file, cfg.w, cfg.h);
      if (!blob) throw new Error("resize_failed");
      const ts = Date.now();
      const path = `${supabaseUserId}/${ts}.jpg`;

      // List previous objects in our folder to clean up after success
      let oldPaths = [];
      try {
        const { data: list } = await supabase.storage.from(bucket).list(supabaseUserId, { limit: 50 });
        oldPaths = (list || []).map((f) => `${supabaseUserId}/${f.name}`);
      } catch (_e) { /* noop */ }

      // Upload new file
      const { error: upErr } = await supabase.storage.from(bucket).upload(path, blob, {
        contentType: "image/jpeg",
        cacheControl: "31536000",
        upsert: false,
      });
      if (upErr) throw upErr;

      // Public URL
      const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
      const url = pub?.publicUrl;
      if (!url) throw new Error("no_public_url");

      // Persist to profiles row
      const { error: profErr } = await supabase.from("profiles").update({
        [cfg.profile_col]: url,
        updated_at: new Date().toISOString(),
      }).eq("user_id", supabaseUserId);
      if (profErr) throw profErr;

      // Clean up old files (best-effort)
      if (oldPaths.length) {
        supabase.storage.from(bucket).remove(oldPaths).catch(() => {});
      }

      setPreviewUrl(url);
      toast.success(isDE ? "Bild aktualisiert" : "Image updated");
      onUploaded?.(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[uploader]", bucket, "failed", e);
      toast.error(isDE ? "Upload fehlgeschlagen" : "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeImage = async () => {
    if (!ready || !supabaseUserId) return;
    if (!confirm(isDE ? "Bild wirklich entfernen?" : "Remove image?")) return;
    setBusy(true);
    try {
      // List & wipe folder
      try {
        const { data: list } = await supabase.storage.from(bucket).list(supabaseUserId, { limit: 50 });
        const paths = (list || []).map((f) => `${supabaseUserId}/${f.name}`);
        if (paths.length) await supabase.storage.from(bucket).remove(paths);
      } catch (_e) { /* noop */ }
      await supabase.from("profiles").update({
        [cfg.profile_col]: null,
        updated_at: new Date().toISOString(),
      }).eq("user_id", supabaseUserId);
      setPreviewUrl(null);
      onUploaded?.(null);
      toast.success(isDE ? "Bild entfernt" : "Image removed");
    } catch (_e) {
      toast.error(isDE ? "Entfernen fehlgeschlagen" : "Remove failed");
    } finally { setBusy(false); }
  };

  // ----- Render -----
  return (
    <div
      className={isAvatar ? "" : "block"}
      data-testid={`uploader-${bucket}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid={`uploader-${bucket}-input`}
      />

      {isAvatar ? (
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-zinc-800 border-2 border-white/10 overflow-hidden grid place-items-center" data-testid="uploader-avatar-preview">
              {previewUrl
                ? <img src={previewUrl} alt="avatar" className="w-full h-full object-cover" />
                : <Camera size={22} className="text-zinc-500" />}
            </div>
            {busy && (
              <div className="absolute inset-0 rounded-full bg-black/60 grid place-items-center">
                <Loader2 className="animate-spin text-neon" size={18} />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button
              type="button" size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={busy || !ready}
              className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold h-8 text-xs"
              data-testid="uploader-avatar-pick"
            >
              <ImagePlus size={12} className="mr-1.5" />
              {previewUrl ? (isDE ? "Avatar ändern" : "Change avatar") : (isDE ? "Avatar hochladen" : "Upload avatar")}
            </Button>
            {previewUrl && (
              <Button
                type="button" size="sm" variant="ghost"
                onClick={removeImage}
                disabled={busy}
                className="text-zinc-400 hover:text-rose-300 h-7 text-[11px] justify-start px-2"
                data-testid="uploader-avatar-remove"
              >
                <Trash2 size={11} className="mr-1.5" /> {isDE ? "Entfernen" : "Remove"}
              </Button>
            )}
            <p className="text-[10px] text-zinc-500">
              {isDE ? "JPG/PNG/WebP · max. 8 MB · wird auf 512×512 zugeschnitten." : "JPG/PNG/WebP · max 8 MB · cropped to 512×512."}
            </p>
          </div>
        </div>
      ) : (
        <div className="relative">
          <div
            className="relative w-full aspect-[3/1] rounded-2xl overflow-hidden bg-gradient-to-r from-fuchsia-500/15 via-cyan-500/15 to-emerald-500/15 border border-white/10"
            data-testid="uploader-banner-preview"
          >
            {previewUrl && <img src={previewUrl} alt="banner" className="w-full h-full object-cover" />}
            {busy && (
              <div className="absolute inset-0 bg-black/60 grid place-items-center">
                <Loader2 className="animate-spin text-neon" size={24} />
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent flex gap-2">
              <Button
                type="button" size="sm"
                onClick={() => inputRef.current?.click()}
                disabled={busy || !ready}
                className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold h-8 text-xs"
                data-testid="uploader-banner-pick"
              >
                <ImagePlus size={12} className="mr-1.5" />
                {previewUrl ? (isDE ? "Banner ändern" : "Change banner") : (isDE ? "Banner hochladen" : "Upload banner")}
              </Button>
              {previewUrl && (
                <Button
                  type="button" size="sm" variant="ghost"
                  onClick={removeImage}
                  disabled={busy}
                  className="text-white/80 hover:text-rose-300 h-8 text-xs bg-black/40 hover:bg-black/60"
                  data-testid="uploader-banner-remove"
                >
                  <Trash2 size={12} className="mr-1.5" /> {isDE ? "Entfernen" : "Remove"}
                </Button>
              )}
            </div>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1">
            {isDE ? "Optimal 1500×500 · wird automatisch zugeschnitten." : "Optimal 1500×500 · auto-cropped."}
          </p>
        </div>
      )}
    </div>
  );
}
